import jsPDF from "jspdf";

const nameA = (map) => (map?.partner_a_name || "").trim() || "Partner A";
const nameB = (map) => (map?.partner_b_name || "").trim() || "Partner B";

function fmtUnaddressed(items, map) {
  return (items || [])
    .map((it) => {
      if (typeof it === "string") return it;
      const side =
        it?.side === "yours" ? ` (${nameA(map)})` : it?.side === "other" ? ` (${nameB(map)})` : it?.side === "both" ? " (both)" : "";
      return `${it?.need || ""}${side}`;
    })
    .join(", ");
}

export function buildMarkdown(map) {
  const A = nameA(map);
  const B = nameB(map);
  const lines = [];
  lines.push(`# ${map.title || map.issue || "Our session"}`);
  lines.push("");
  lines.push(`## The disagreement`);
  lines.push(map.issue || "—");
  lines.push("");
  lines.push(`## Where you each stand`);
  lines.push(`**${A}:** ${map.position_a || "—"}`);
  lines.push(`**${B}:** ${map.position_b || "—"}`);
  if (map.concerns_a) {
    lines.push("");
    lines.push(`**What ${A} feels underneath:**`);
    lines.push(map.concerns_a);
  }
  if (map.concerns_b) {
    lines.push("");
    lines.push(`**What ${B} feels underneath:**`);
    lines.push(map.concerns_b);
  }
  lines.push("");
  lines.push(`## What you each need`);
  lines.push(`**${A}'s needs:**`);
  (map.needs_a || []).forEach((n) => lines.push(`- ${n}`));
  lines.push("");
  lines.push(`**${B}'s needs:**`);
  (map.needs_b || []).forEach((n) => lines.push(`- ${n}`));
  lines.push("");
  lines.push(`## Ways forward`);
  (map.solutions || []).forEach((s, i) => {
    lines.push(`### ${i + 1}. ${s.title}`);
    lines.push(s.description || "");
    if (s.needs_satisfied_a?.length) lines.push(`- Meets ${A}'s needs: ${s.needs_satisfied_a.join(", ")}`);
    if (s.needs_satisfied_b?.length) lines.push(`- Meets ${B}'s needs: ${s.needs_satisfied_b.join(", ")}`);
    if (s.needs_unaddressed?.length) lines.push(`- Not yet addressed: ${fmtUnaddressed(s.needs_unaddressed, map)}`);
    if (s.improvements) lines.push(`- Could be even better: ${s.improvements}`);
    lines.push("");
  });

  const tips = map.communication_tips || {};
  if ((tips.for_a?.length || tips.for_b?.length || tips.together?.length)) {
    lines.push(`## How to talk about it`);
    if (tips.for_a?.length) {
      lines.push(`**For ${A} to say:**`);
      tips.for_a.forEach((t) => lines.push(`- ${t}`));
    }
    if (tips.for_b?.length) {
      lines.push(`**For ${B} to say:**`);
      tips.for_b.forEach((t) => lines.push(`- ${t}`));
    }
    if (tips.together?.length) {
      lines.push(`**Ground rules for both:**`);
      tips.together.forEach((t) => lines.push(`- ${t}`));
    }
    lines.push("");
  }

  lines.push(`## Reflection`);
  const r = map.reflection || {};
  lines.push(`**Did this help you understand each other better? (1–5):** ${r.change_rating || "—"}`);

  if ((map.history || []).length > 0) {
    lines.push("");
    lines.push(`## Earlier versions (before discussion changes)`);
    map.history.forEach((h, idx) => {
      lines.push("");
      lines.push(`### Version ${idx + 1}${h.archived_at ? ` — ${new Date(h.archived_at).toLocaleString()}` : ""}`);
      lines.push(`**${A}:** ${h.position_a || "—"}`);
      lines.push(`**${B}:** ${h.position_b || "—"}`);
      lines.push(`**${A}'s needs:** ${(h.needs_a || []).join(", ") || "—"}`);
      lines.push(`**${B}'s needs:** ${(h.needs_b || []).join(", ") || "—"}`);
      lines.push(`**Ways forward:**`);
      (h.solutions || []).forEach((s, i) => lines.push(`  ${i + 1}. ${s.title} — ${s.description || ""}`));
    });
  }
  return lines.join("\n");
}

export function downloadText(map) {
  const md = buildMarkdown(map);
  const blob = new Blob([md], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${(map.issue || "between-us").replace(/\s+/g, "-").toLowerCase()}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}

export async function copyMarkdown(map) {
  const text = buildMarkdown(map);
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) {
    // fall through to legacy fallback
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (e) {
    return false;
  }
}

export function exportPdf(map) {
  const A = nameA(map);
  const B = nameB(map);
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const margin = 48;
  const width = doc.internal.pageSize.getWidth() - margin * 2;
  let y = margin;

  const addPageIfNeeded = (h) => {
    if (y + h > doc.internal.pageSize.getHeight() - margin) {
      doc.addPage();
      y = margin;
    }
  };

  const writeBlock = (text, { size = 11, bold = false, color = [45, 42, 38], gap = 6 } = {}) => {
    doc.setFont("times", bold ? "bold" : "normal");
    doc.setFontSize(size);
    doc.setTextColor(...color);
    const wrapped = doc.splitTextToSize(text || "—", width);
    wrapped.forEach((line) => {
      addPageIfNeeded(size + 4);
      doc.text(line, margin, y);
      y += size + 4;
    });
    y += gap;
  };

  writeBlock(map.title || map.issue || "Our session", { size: 20, bold: true, color: [74, 93, 78], gap: 10 });
  writeBlock("The disagreement", { size: 13, bold: true, color: [74, 93, 78], gap: 2 });
  writeBlock(map.issue);
  writeBlock("Where you each stand", { size: 13, bold: true, color: [74, 93, 78], gap: 2 });
  writeBlock(`${A}: ${map.position_a || "—"}`);
  writeBlock(`${B}: ${map.position_b || "—"}`);
  writeBlock("What you each need", { size: 13, bold: true, color: [74, 93, 78], gap: 2 });
  writeBlock(`${A}: ${(map.needs_a || []).join(", ") || "—"}`);
  writeBlock(`${B}: ${(map.needs_b || []).join(", ") || "—"}`);
  writeBlock("Ways forward", { size: 13, bold: true, color: [74, 93, 78], gap: 2 });
  (map.solutions || []).forEach((s, i) => {
    writeBlock(`${i + 1}. ${s.title}`, { bold: true, gap: 2 });
    writeBlock(s.description);
    if (s.needs_satisfied_a?.length) writeBlock(`Meets ${A}: ${s.needs_satisfied_a.join(", ")}`, { size: 10, color: [110, 104, 96] });
    if (s.needs_satisfied_b?.length) writeBlock(`Meets ${B}: ${s.needs_satisfied_b.join(", ")}`, { size: 10, color: [110, 104, 96] });
    if (s.needs_unaddressed?.length) writeBlock(`Not yet addressed: ${fmtUnaddressed(s.needs_unaddressed, map)}`, { size: 10, color: [110, 104, 96] });
    if (s.improvements) writeBlock(`Could be even better: ${s.improvements}`, { size: 10, color: [110, 104, 96] });
  });

  const tips = map.communication_tips || {};
  if (tips.for_a?.length || tips.for_b?.length || tips.together?.length) {
    writeBlock("How to talk about it", { size: 13, bold: true, color: [74, 93, 78], gap: 2 });
    if (tips.for_a?.length) { writeBlock(`For ${A} to say:`, { bold: true, gap: 2 }); tips.for_a.forEach((t) => writeBlock(`• ${t}`, { size: 10, color: [110, 104, 96] })); }
    if (tips.for_b?.length) { writeBlock(`For ${B} to say:`, { bold: true, gap: 2 }); tips.for_b.forEach((t) => writeBlock(`• ${t}`, { size: 10, color: [110, 104, 96] })); }
    if (tips.together?.length) { writeBlock("Ground rules:", { bold: true, gap: 2 }); tips.together.forEach((t) => writeBlock(`• ${t}`, { size: 10, color: [110, 104, 96] })); }
  }

  const r = map.reflection || {};
  writeBlock("Reflection", { size: 13, bold: true, color: [74, 93, 78], gap: 2 });
  writeBlock(`Did this help you understand each other better? (1–5): ${r.change_rating || "—"}`);

  doc.save(`${(map.issue || "between-us").replace(/\s+/g, "-").toLowerCase()}.pdf`);
}
